import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";

interface Props { payload: VisualPayload }

const LEAGUE_COLOURS: Record<string, { badge: string; accent: string; label: string }> = {
  great:  { badge: "bg-green-500/20 text-green-400 border-green-500/30",  accent: "text-green-400",  label: "Great League" },
  ultra:  { badge: "bg-blue-500/20 text-blue-400 border-blue-500/30",    accent: "text-blue-400",   label: "Ultra League" },
  master: { badge: "bg-purple-500/20 text-purple-400 border-purple-500/30", accent: "text-purple-400", label: "Master League" },
};

function leagueMeta(league: string) {
  return LEAGUE_COLOURS[league?.toLowerCase()] ?? {
    badge: "bg-white/10 text-white/50 border-white/20",
    accent: "text-scout-yellow",
    label: league ?? "",
  };
}

function IVSection({ sec, showLeague }: { sec: Record<string, unknown>; showLeague: boolean }) {
  const rank = sec.rank as number | undefined;
  const total = sec.total_combinations as number | undefined;
  const pctile = rank && total ? (((total - rank) / total) * 100).toFixed(1) : null;
  const league = (sec.league as string | undefined) ?? "";
  const meta = leagueMeta(league);

  return (
    <div className="space-y-3">
      {/* League badge */}
      {showLeague && league && (
        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.badge}`}>
          {meta.label}
        </span>
      )}

      {/* Rank hero */}
      <div className="flex items-end gap-3">
        <span className={`text-4xl font-bold tabular-nums ${meta.accent}`}>#{rank?.toLocaleString()}</span>
        <div className="pb-1">
          <p className="text-xs text-white/40">of {total?.toLocaleString()}</p>
          {pctile && <p className="text-xs text-green-400 font-medium">Top {(100 - parseFloat(pctile)).toFixed(1)}%</p>}
        </div>
      </div>

      {/* IV grid */}
      <div className="grid grid-cols-3 gap-2">
        {([
          ["ATK", sec.iv_attack as number],
          ["DEF", sec.iv_defense as number],
          ["STA", sec.iv_stamina as number],
        ] as [string, number][]).map(([label, value]) => (
          <div key={label} className="bg-white/[0.04] rounded-xl py-2.5 text-center border border-white/[0.06]">
            <p className="text-[11px] text-white/30 uppercase tracking-wide">{label}</p>
            <p className="text-lg font-bold text-white mt-0.5">{value}</p>
            <p className="text-[10px] text-white/25">/15</p>
          </div>
        ))}
      </div>

      {/* Footer meta */}
      <div className="flex items-center gap-3 text-xs text-white/40">
        <span>Lv {sec.level as number}</span>
        <span className="text-white/20">·</span>
        <span>CP {(sec.cp as number).toLocaleString()}</span>
        <span className="text-white/20">·</span>
        <span>SP {(sec.stat_product as number).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
      </div>
    </div>
  );
}

export default function IVCard({ payload }: Props) {
  const sections = payload.sections.filter((s) => s.section === "iv_rank");
  const isMultiLeague = sections.length > 1;

  if (sections.length === 0) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40">No IV data.</p>
      </CardShell>
    );
  }

  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      {isMultiLeague ? (
        <div className="space-y-5">
          {sections.map((sec, i) => (
            <div key={sec.league as string ?? i}>
              <IVSection sec={sec} showLeague />
              {i < sections.length - 1 && <div className="mt-5 border-t border-white/[0.06]" />}
            </div>
          ))}
        </div>
      ) : (
        <IVSection sec={sections[0]} showLeague={false} />
      )}
    </CardShell>
  );
}
